import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import StandardLayout from '../../../components/layout/StandardLayout';
import { getExams, getExamSubcategory, updateExamSubcategory } from '../../../services/examAPIService';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useAlert } from '../../../context/AlertContext';

const ExamSubcategoryEditForm = () => {
    const { id } = useParams();
    const [formData, setFormData] = useState({
        title: '',
        exam_id: ''
    });
    const [exams, setExams] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const navigate = useNavigate();
    const { showAlert } = useAlert();

    const loadData = useCallback(async () => {
        try {
            setLoading(true);
            // Lade die Prüfungsteil-Daten
            const subcategoryData = await getExamSubcategory(id);
            // Lade alle verfügbaren Prüfungen
            const examsData = await getExams();
            
            setExams(examsData);
            setFormData({
                title: subcategoryData.title,
                exam_id: subcategoryData.exam_id.toString()
            });
            setError(null);
        } catch (err) {
            setError('Fehler beim Laden der Daten');
            console.error('Error loading data:', err);
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const submitData = {
                title: formData.title,
                exam_id: parseInt(formData.exam_id)
            };
            await updateExamSubcategory(id, submitData);
            showAlert('Prüfungsteil wurde erfolgreich aktualisiert', 'success', true);
            navigate('/admin/exam-parts');
        } catch (error) {
            console.error('Error updating exam subcategory:', error);
            setError('Fehler beim Aktualisieren des Prüfungsteils');
        }
    };

    const handleCancel = () => {
        navigate('/admin/exam-parts');
    };

    if (loading) {
        return (
            <StandardLayout>
                <div className="flex justify-center items-center min-h-screen">
                    <span className="loading loading-spinner loading-lg text-primary"></span>
                </div>
            </StandardLayout>
        );
    }

    return (
        <StandardLayout>
            <div className="container mx-auto px-4 py-8">
                <div className="max-w-2xl mx-auto">
                    <h1 className="text-2xl font-bold mb-6">Prüfungsteil bearbeiten</h1>
                    
                    {error && (
                        <div className="alert alert-error mb-4">
                            <XMarkIcon className="w-6 h-6" />
                            <span>{error}</span>
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div className="form-control">
                            <label className="label">
                                <span className="label-text">Prüfung</span>
                            </label>
                            <select
                                name="exam_id"
                                value={formData.exam_id}
                                onChange={handleInputChange}
                                className="select select-bordered w-full"
                                required
                            >
                                <option value="">Prüfung auswählen</option>
                                {exams.map(exam => (
                                    <option key={exam.id} value={exam.id}>
                                        {exam.title}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="form-control">
                            <label className="label">
                                <span className="label-text">Titel</span>
                            </label>
                            <input
                                type="text"
                                name="title"
                                value={formData.title}
                                onChange={handleInputChange}
                                className="input input-bordered w-full"
                                placeholder="Geben Sie den Titel des Prüfungsteils ein"
                                required
                            />
                        </div>

                        <div className="flex justify-end gap-4">
                            <button
                                type="button"
                                onClick={handleCancel}
                                className="btn btn-ghost"
                            >
                                Abbrechen
                            </button>
                            <button
                                type="submit"
                                className="btn btn-primary"
                            >
                                Speichern
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </StandardLayout>
    );
};

export default ExamSubcategoryEditForm; 